// AddSchedule.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const inp = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const lbl = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '14px',
  color: '#444',
  fontWeight: 600
};

// ✅ Normalize any date format to YYYY-MM-DD string for reliable comparison
const toDateStr = (d) => {
  if (!d) return '';
  return new Date(d).toISOString().split('T')[0];
};

const AddSchedule = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [dupError, setDupError] = useState('');

  const [form, setForm] = useState({
    exam_name: '',
    exam_type: '',
    start_date: '',
    end_date: '',
    center_id: '',
    status: 'active',
    remuneration_type: 'per_shift'
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [centerRes, schedRes] = await Promise.all([
          axios.get('/exam-centers'),
          axios.get('/schedules')
        ]);
        setCenters(centerRes.data || []);
        setExistingSchedules(schedRes.data || []);
      } catch (err) {
        console.log(err);
        toast.error('Failed to load data');
      }
    };
    fetchAll();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setDupError('');
  };

  const checkDuplicate = () => {
    const { exam_name, exam_type, center_id, start_date, end_date } = form;
    if (!start_date || !end_date || !exam_name) return false;

    // Normalize new form dates to plain YYYY-MM-DD
    const newStart = toDateStr(start_date);
    const newEnd   = toDateStr(end_date);

    return existingSchedules.some((s) => {
      const sameName = s.exam_name?.toLowerCase().trim() === exam_name.toLowerCase().trim();
      const sameType = (s.exam_type || '').toLowerCase().trim() === (exam_type || '').toLowerCase().trim();
      const sameCenter =
        (center_id === '' || center_id === null)
          ? !s.center_id
          : String(s.center_id) === String(center_id);

      // ✅ Normalize DB dates (handles ISO "2026-05-25T18:30:00.000Z") to YYYY-MM-DD
      const exStart = toDateStr(s.start_date);
      const exEnd   = toDateStr(s.end_date);

      // Overlap: new range overlaps existing range
      const overlaps = newStart <= exEnd && newEnd >= exStart;

      return sameName && sameType && sameCenter && overlaps;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDupError('');

    if (form.start_date > form.end_date) {
      toast.error('End date must be after start date');
      return;
    }

    if (checkDuplicate()) {
      setDupError('A schedule with the same exam name, type, center, and overlapping dates already exists.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        center_id: form.center_id === '' ? null : Number(form.center_id)
      };
      await axios.post('/schedules', payload);
      toast.success('Schedule created successfully!');
      navigate('/schedules');
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '32px', backgroundColor: '#f5f6fa', minHeight: '100vh' }}>
      <div style={{ maxWidth: '620px', margin: '0 auto' }}>

        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <button
            onClick={() => navigate('/schedules')}
            style={{
              background: 'none', border: '1px solid #ddd',
              padding: '6px 14px', borderRadius: '8px',
              cursor: 'pointer', fontSize: '14px', color: '#555'
            }}
          >
            ← Back
          </button>
          <h2 style={{ color: '#f1870e', margin: 0, fontSize: '22px', fontWeight: 700 }}>
            Add Schedule
          </h2>
        </div>

        {/* FORM CARD */}
        <div style={{
          backgroundColor: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '32px'
        }}>

          {dupError && (
            <div style={{
              backgroundColor: '#ffebee', color: '#c62828',
              border: '1px solid #ef9a9a', borderRadius: '8px',
              padding: '12px 16px', marginBottom: '20px',
              fontSize: '14px', fontWeight: 500
            }}>
              ⚠️ {dupError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Exam Name *</label>
                <input
                  type="text"
                  name="exam_name"
                  value={form.exam_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Final Semester Exam 2026"
                  style={{ ...inp, border: dupError ? '1px solid #ef9a9a' : '1px solid #ddd' }}
                />
              </div>

              {/* Remuneration Calculation Type */}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Remuneration Calculation Type *</label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {[
                    { value: 'per_shift', label: 'Per Shift', hint: 'Staff can differ per shift. Each person is paid for every shift they attend.' },
                    { value: 'per_day', label: 'Per Day', hint: 'Same staff work every shift that day. Each person is paid once per day, not per shift.' }
                  ].map(opt => (
                    <label
                      key={opt.value}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        padding: '12px 14px',
                        border: `1.5px solid ${form.remuneration_type === opt.value ? '#f1870e' : '#ddd'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: form.remuneration_type === opt.value ? '#fff3e0' : 'white'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: '#f1870e' }}>
                        <input
                          type="radio"
                          name="remuneration_type"
                          value={opt.value}
                          checked={form.remuneration_type === opt.value}
                          onChange={handleChange}
                        />
                        {opt.label}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>{opt.hint}</span>
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                  This cannot be easily changed once shifts have staff assigned — pick carefully.
                </p>
              </div>

              <div>
                <label style={lbl}>Exam Type</label>
                <input
                  type="text"
                  name="exam_type"
                  value={form.exam_type}
                  onChange={handleChange}
                  placeholder="e.g. Theory, Practical"
                  style={inp}
                />
              </div>

              <div>
                <label style={lbl}>Exam Center</label>
                <select
                  name="center_id"
                  value={form.center_id}
                  onChange={handleChange}
                  style={{ ...inp, backgroundColor: 'white' }}
                >
                  <option value="">Select Center (Optional)</option>
                  {centers.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.center_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={lbl}>Start Date *</label>
                <input
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleChange}
                  required
                  style={inp}
                />
              </div>

              <div>
                <label style={lbl}>End Date *</label>
                <input
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleChange}
                  required
                  style={inp}
                />
              </div>

              <div>
                <label style={lbl}>Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  style={{ ...inp, backgroundColor: 'white' }}
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: '#f1870e', color: 'white',
                  border: 'none', borderRadius: '8px',
                  fontSize: '15px', fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Creating...' : 'Create Schedule'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/schedules')}
                style={{
                  padding: '12px 24px', backgroundColor: 'white',
                  color: '#666', border: '1px solid #ddd',
                  borderRadius: '8px', fontSize: '15px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSchedule;